import { Injectable } from '@angular/core';
import { of as observableOf, Observable } from 'rxjs';

// HISNG
import { HisBackendService } from 'hisng';

import { Patent } from 'app/fs/res/patent/model/patent/patent';
import { PatentInventor } from '../../model/patent/patent-classes';

@Injectable()
export class PatentService {

    constructor( private backendService: HisBackendService ) { }

    /**
     * get patent with given id.
     *
     * @param id Id of patent to load.
     */
    getPatentById( id: number ): Observable<Patent> {
        return this.backendService.getData( `api/v1/fs/res/patent/${id}` );
    }

    /**
     * get patent with details (includes all available listview content)
     *
     * @param id - id of the patent
     */
    getPatentWithDetailsById( id: number ): Observable<Patent> {
        return this.backendService.getData( `api/v1/fs/res/patent/${id}/details` );
    }

    /**
     * save new or existing patent data.
     *
     * @param patent Patent to save.
     * @return Observable.of( updated patent )
     */
    savePatent( patent: Patent ): Observable<Patent> {
        let savedPatent: Observable<Patent>;
        if ( patent.id == null ) {
            savedPatent = this.backendService.postData( 'api/v1/fs/res/patent', patent );
        } else {
            savedPatent = this.backendService.putData( `api/v1/fs/res/patent/${patent.id}`, patent );
        }
        return savedPatent;
    }

    saveExternalPersonInventor( patentInventor: PatentInventor ): Observable<PatentInventor> {
        return this.backendService.postData( 'api/v1/fs/res/patent/patentInventor', patentInventor );
    }

    // create new patent
    getNewPatent(): Observable<Patent> {
        return observableOf( new Patent() );
    }

    /**
     * delete patent from database.
     *
     * @param patentId Id of patent to delete.
     * @return Observable.of( 0=fail / 1=success )
     */
    deletePatent( patentId: number ): Observable<number> {
        return this.backendService.delete( `api/v1/fs/res/patent/${patentId}` );
    }
    /**
     * check if patent can be deleted and has not further references to other objects.
     *
     * @param patentId Id of patent
     */
    canDeletePatent( patentId: number ): Observable<number> {
        return this.backendService.getData( `api/v1/fs/res/canDeletePatent/${patentId}` );
    }

    /**
     * get all sub patents for priority patent
     *
     * @param patentId priority patent id
     */
    getPatentFamily( patentId: number ) {
        return this.backendService.getData( `api/v1/fs/res/patent/findChildPatents/${patentId}` );
    }

    /**
     * get patents by text from database
     *
     * @param searchText contained in patent
     */
    getValues( searchText: string ): Observable<Patent[]> {
        return this.backendService.getData( 'api/v1/fs/res/patent' ); // ?q=${searchText}
    }

    /**
     * get all patents containing project with given Id
     *
     * @param projectId Id of project
     */
    getPatentsForProject( projectId: number ): Observable<Patent[]> {
        return this.backendService.getData( `api/v1//fs/res/patent/findPatentsByProjectId/${projectId}` );
    }

    canViewPatent( id: number ): Observable<boolean> {
        return this.backendService.postData(
            `api/v1/fs/res/hasRightsOnObject/Patent/${id}/true`,
            [
                'RIGHT_FS_RES_PATENT_VIEW_PATENT',
                'RIGHT_FS_RES_PATENT_VIEW_PATENT_OWN',
                'RIGHT_FS_RES_PATENT_VIEW_PATENT_BY_TYPE',
                'RIGHT_FS_RES_PATENT_VIEW_PATENT_VISIBLE'
            ] );
    }

    canEditPatent( id: number ): Observable<boolean> {
        return this.backendService.postData(
            `api/v1/fs/res/hasRightsOnObject/Patent/${id}/true`,
            ['RIGHT_FS_RES_PATENT_EDIT_PATENT']
        );
    }
}
